import { api } from '@/services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';

// --- Types ---
export interface WorkflowRunStageOut {
  id: string;
  run_id: string;
  stage_id: string;
  seq: number;
  system_base: string | null;
  system_enviado: string | null;
  user_enviado: string | null;
  params_enviados: Record<string, unknown> | null;
  entrada_estruturada: Record<string, unknown> | null;
  saida_texto: string | null;
  saida_estruturada: Record<string, unknown> | null;
  model_pedido: string | null;
  model_respondido: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  tokens_cached: number | null;
  custo_usd: number | null;
  custo_origem: string | null;
  duracao_ms: number | null;
  erro: string | null;
  started_at: string | null;
  finished_at: string | null;
  payload_expurgado_em: string | null;
}

export interface RunDetalhe {
  id: string;
  client_id: string;
  workflow_name: string;
  vertical: string;
  origem: string;
  wp_site: string | null;
  wp_post_id: number | null;
  wp_link: string | null;
  derivado_de: string | null;
  from_stage: string | null;
  overrides_aplicados: Record<string, unknown> | null;
  custo_usd: number | null;
  custo_estagios_usd: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  status: string;
  chave_unicidade: string | null;
  entrada: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  payload_expurgado_em: string | null;
  stages: WorkflowRunStageOut[];
}

export interface ArtigoDoWorkflow {
  id: string;
  vertical: string;
  titulo: string;
  wp_link: string | null;
  wp_post_id: number | null;
  status: string;
  created_at: string;
  tem_run: boolean;
  vertical_inferida: boolean;
}

export interface WorkflowRunEngenharia {
  id: string;
  client_id: string;
  workflow_name: string;
  status: string;
  chave_unicidade: string | null;
  entrada: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  payload_expurgado_em: string | null;
  stages: WorkflowRunStageOut[];
}

export interface RerunReq {
  from_stage: string;
  overrides: Record<string, { system?: string; params?: Record<string, unknown> }>;
}

export interface PublicarReq {
  status: 'draft' | 'publish';
}

export interface Vertical {
  id: string;
  nome: string;
  habilitada: boolean;
  motivo: string | null;
  prontidao: Array<{ requisito: string; ok: boolean; detalhe: string }>;
  run_suportado: boolean;
  retencao_dias: number;
  editoria_fabrica_slug: string | null;
}

export interface Molde {
  disponivel: boolean;
  motivo?: string;
  site?: { nome: string; url: string };
  categorias?: string[];
  perene_existente?: { wp_post_id: number; wp_link: string; editoria: string };
}

export interface ResultadoLinha {
  wp_post_id: number;
  titulo: string;
  pageviews: number;
  sessions: number;
  gsc_clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  collected_at: string;
}

export interface Resultado {
  configurado: boolean;
  motivo?: string;
  sync_em?: string;
  linhas: ResultadoLinha[];
}

// --- Keys ---
export const workflows2Keys = {
  all: ['workflows2'] as const,
  artigos: (site: string | null, vertical: string | null) =>
    [...workflows2Keys.all, 'artigos', site, vertical] as const,
  verticais: (site: string | null) => [...workflows2Keys.all, 'verticais', site] as const,
  molde: (site: string | null) => [...workflows2Keys.all, 'molde', site] as const,
  resultado: (site: string | null, vertical: string | null) =>
    [...workflows2Keys.all, 'resultado', site, vertical] as const,
  engenhariaRuns: (client: string | null, site: string | null, vertical: string | null) =>
    [...workflows2Keys.all, 'engenhariaRuns', client, site, vertical] as const,
  engenhariaRunDetalhe: (id: string) =>
    [...workflows2Keys.all, 'engenhariaRunDetalhe', id] as const,
};

// --- Hooks ---
export function useArtigos(
  site: string | null,
  vertical: string | null,
  options?: Omit<UseQueryOptions<ArtigoDoWorkflow[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: workflows2Keys.artigos(site, vertical),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (site) qs.append('site', site);
      if (vertical) qs.append('vertical', vertical);
      return api.get<ArtigoDoWorkflow[]>(`/workflows2/artigos?${qs.toString()}`);
    },
    enabled: !!site && !!vertical,
    ...options,
  });
}

export function useVerticais(
  site: string | null,
  options?: Omit<UseQueryOptions<Vertical[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: workflows2Keys.verticais(site),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (site) qs.append('site', site);
      const suffix = qs.toString();
      return api.get<Vertical[]>(`/workflows2/verticais${suffix ? `?${suffix}` : ''}`);
    },
    ...options,
  });
}

export function useMolde(site: string | null) {
  return useQuery({
    queryKey: workflows2Keys.molde(site),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (site) qs.append('site', site);
      return api.get<Molde>(`/workflows2/molde?${qs.toString()}`);
    },
    enabled: !!site,
    retry: false,
  });
}

export function useResultado(site: string | null, vertical: string | null, days: number = 30) {
  return useQuery({
    queryKey: workflows2Keys.resultado(site, vertical),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (site) qs.append('site', site);
      if (vertical) qs.append('vertical', vertical);
      qs.append('days', days.toString());
      return api.get<Resultado>(`/workflows2/resultado?${qs.toString()}`);
    },
    enabled: !!site && !!vertical,
    retry: false,
  });
}

export function useEngenhariaRuns(
  client: string | null,
  site: string | null,
  vertical: string | null,
) {
  return useQuery({
    queryKey: workflows2Keys.engenhariaRuns(client, site, vertical),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (client) qs.append('client', client);
      if (site) qs.append('site', site);
      if (vertical) qs.append('vertical', vertical);
      return api.get<WorkflowRunEngenharia[]>(`/workflows2/engenharia/runs?${qs.toString()}`);
    },
    enabled: !!client && !!site,
  });
}

export function useEngenhariaRunDetalhe(id: string | null) {
  return useQuery({
    queryKey: workflows2Keys.engenhariaRunDetalhe(id || ''),
    queryFn: () => api.get<RunDetalhe>(`/workflows2/engenharia/runs/${id}`),
    enabled: !!id,
  });
}

export function useRerunRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RerunReq }) =>
      api.post(`/workflows2/engenharia/runs/${id}/rerun`, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflows2Keys.engenhariaRunDetalhe(id) });
    },
    retry: false,
  });
}

export function usePublicarRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PublicarReq }) =>
      api.post(`/workflows2/engenharia/runs/${id}/publicar`, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflows2Keys.engenhariaRunDetalhe(id) });
    },
    retry: false,
  });
}
